import React, { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ProtectedRoute from '../../routes/protected';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { MultiSelect } from 'primereact/multiselect';
import performanceService from '../../services/data/performance.service';
import UsePerms from '../../context/userAccess';
import useCompanyData from '../../context/company';
import SkeletonTeam from './skeletonTeam';
import { Rating } from 'primereact/rating';
import { getRatingLabel } from '../../helpers/ratingLabels';
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle';

const SCORE_VALUE_MAP = { NA: 0, IN: 1, BE: 2, DE: 3, AE: 4, SE: 5 };

const DISCUSSION_HELP =
    'Marque quando a nota deste item ainda precisa ser debatida no comitê. Não altera a nota calibrada; apenas sinaliza aos demais participantes que o ponto está em aberto.';

function DiscussionTh() {
    return (
        <th className="text-center" style={{ width: '90px' }}>
            <span className="d-inline-flex align-items-center justify-content-center gap-1">
                Discussão
                <FontAwesomeIcon
                    icon={solid('circle-info')}
                    className="text-muted"
                    style={{ fontSize: '0.85rem', cursor: 'help' }}
                    data-bs-toggle="tooltip"
                    data-bs-custom-class="custom-tooltip"
                    data-bs-placement="top"
                    data-bs-title={DISCUSSION_HELP}
                    role="img"
                    aria-label={DISCUSSION_HELP}
                />
            </span>
        </th>
    );
}

function formatOriginalScoreValue(item) {
    if (item.original_score_value != null && item.original_score_value !== '') {
        return Number(item.original_score_value).toFixed(1);
    }
    const value = SCORE_VALUE_MAP[item.original_score];
    if (value != null) return Number(value).toFixed(1);
    return item.original_score || '—';
}

const QUADRANT_LABELS = {
    1: 'Detratores', 2: 'Questionáveis', 3: 'Latentes',
    4: 'Eficazes', 5: 'Transitórios', 6: 'Emergentes',
    7: 'Comprometidos', 8: 'Emergentes', 9: 'Estrelas',
};

function toDatetimeLocalValue(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function ratingToScoreCode(val) {
    const map = { 0: 'NA', 1: 'IN', 2: 'BE', 3: 'DE', 4: 'AE', 5: 'SE' };
    return map[val] ?? '';
}

function ScoreSelect({ value, onChange, disabled, company }) {
    const ratingValue = value != null && value in SCORE_VALUE_MAP
        ? SCORE_VALUE_MAP[value]
        : null;

    return (
        <div className="d-flex align-middle justify-content-center flex-wrap">
            <Rating
                value={ratingValue}
                readOnly={disabled}
                cancel={false}
                stars={5}
                onChange={(e) => onChange({ target: { value: ratingToScoreCode(e.value) } })}
            />
            {ratingValue != null ? (
                <span className="ps-2 pt-1">
                    {ratingValue === 0 ? 'Não Aplicável' : getRatingLabel(company, ratingValue)}
                </span>
            ) : null}
        </div>
    );
}

function ItemTable({ title, items, onItemChange, requiresJustification, readOnly, company, showComments = false }) {
    const [commentsOpenKey, setCommentsOpenKey] = useState(null);
    const tableRef = useRef(null);

    useEffect(() => {
        if (!tableRef.current) return undefined;
        const triggers = tableRef.current.querySelectorAll('[data-bs-toggle="tooltip"]');
        const instances = [...triggers].map((el) => new bootstrap.Tooltip(el));
        return () => instances.forEach((instance) => instance.dispose());
    }, [items, readOnly, showComments]);

    if (!items?.length) return null;
    return (
        <div className="mb-4">
            <h6 className="fw-bold text-green">{title}</h6>
            <div ref={tableRef} className="table-responsive rounded overflow-hidden border">
                <table className="table table-sm align-middle">
                    <thead className="table-light">
                        <tr className="align-middle">
                            <th style={{ width: readOnly && showComments ? '50%' : '30%' }}>Item</th>
                            <th
                                className="text-center"
                                colSpan={showComments ? 2 : 1}
                            >
                                Nota Original
                            </th>
                            {!readOnly && <th className="text-center" style={{ width: '180px' }}>Nota Calibrada</th>}
                            {!readOnly && <DiscussionTh />}
                            {!readOnly && <th className="text-center">Justificativa</th>}
                            {readOnly && !showComments && <DiscussionTh />}
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, idx) => {
                            const changed = item.original_score !== item.calibrated_score;
                            const inDiscussion = item.in_discussion;
                            const itemCommentsKey = `${title}-${item.item_id}`;
                            return (
                                <Fragment key={item.item_id}>
                                <tr className={`align-middle ${inDiscussion ? 'table-info' : changed ? 'table-success' : ''}`}>
                                    <td>
                                        {item.item_title || item.item_id}
                                        {inDiscussion && <span className="badge bg-info text-dark ms-1">Em discussão</span>}
                                    </td>
                                    <td className="text-center">{formatOriginalScoreValue(item)}</td>
                                    {readOnly && showComments && (
                                        <td className="text-center">
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 text-green"
                                                title={
                                                    commentsOpenKey === itemCommentsKey
                                                        ? 'Ocultar comentário'
                                                        : 'Ver comentário'
                                                }
                                                aria-expanded={commentsOpenKey === itemCommentsKey}
                                                onClick={() => {
                                                    setCommentsOpenKey((prev) =>
                                                        prev === itemCommentsKey ? null : itemCommentsKey
                                                    );
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={
                                                        commentsOpenKey === itemCommentsKey
                                                            ? solid('chevron-up')
                                                            : solid('comments')
                                                    }
                                                />
                                            </button>
                                        </td>
                                    )}
                                    {!readOnly && (
                                        <>
                                            <td>
                                                <ScoreSelect
                                                    value={item.calibrated_score}
                                                    company={company}
                                                    onChange={(e) => onItemChange(idx, 'calibrated_score', e.target.value, title)}
                                                />
                                            </td>
                                            <td className="text-center">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={!!item.in_discussion}
                                                    onChange={(e) => onItemChange(idx, 'in_discussion', e.target.checked, title)}
                                                    title="Marcar como em discussão"
                                                />
                                            </td>
                                            <td>
                                                {changed && requiresJustification ? (
                                                    <textarea
                                                        className="form-control form-control-sm"
                                                        rows={2}
                                                        value={item.justification || ''}
                                                        placeholder="Obrigatório"
                                                        onChange={(e) => onItemChange(idx, 'justification', e.target.value, title)}
                                                        required
                                                    />
                                                ) : changed ? (
                                                    <textarea
                                                        className="form-control form-control-sm"
                                                        rows={2}
                                                        value={item.justification || ''}
                                                        onChange={(e) => onItemChange(idx, 'justification', e.target.value, title)}
                                                    />
                                                ) : (
                                                    <span className="text-muted small">—</span>
                                                )}
                                            </td>
                                        </>
                                    )}
                                    {readOnly && !showComments && (
                                        <td className="text-center">{inDiscussion ? 'Sim' : '—'}</td>
                                    )}
                                </tr>
                                {showComments && commentsOpenKey === itemCommentsKey ? (
                                    <tr className="table-light align-middle">
                                        <td />
                                        <td colSpan={2} className="small border-top-0">
                                            <div className="py-2">
                                                <div className="mb-1 border-bottom border p-2 pb-2 rounded">
                                                    <div className="mt-1">
                                                        <span className="text-muted">Comentário: </span>
                                                        {item.manager_comment ? (
                                                            <span
                                                                className="text-body"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: item.manager_comment,
                                                                }}
                                                            />
                                                        ) : (
                                                            <span className="text-muted fst-italic">
                                                                Sem comentário
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                ) : null}
                                </Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function CalibrationEmployee() {
    const { templateId, employeeId, assessmentId } = useParams();
    const [searchParams] = useSearchParams();
    const reviewScopeId = searchParams.get('review') || '';
    const isStandaloneScope = !templateId || templateId === '0';
    const navigate = useNavigate();
    const VerifyPerms = UsePerms();
    const { rootUser, company } = useCompanyData();
    const toast = useRef(null);
    const draftTimer = useRef(null);

    const [pageLoad, setPageLoad] = useState(true);
    const [detail, setDetail] = useState(null);
    const [objectives, setObjectives] = useState([]);
    const [competencies, setCompetencies] = useState([]);
    const [generalReason, setGeneralReason] = useState('');
    const [committeeMeetingAt, setCommitteeMeetingAt] = useState('');
    const [selectedParticipants, setSelectedParticipants] = useState([]);
    const [eligibleParticipants, setEligibleParticipants] = useState([]);
    const [versions, setVersions] = useState([]);
    const [showVersions, setShowVersions] = useState(false);
    const [preview, setPreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const canAccess = VerifyPerms(['add_performancematrix']) || rootUser;
    const readOnly = detail?.session_status === 'DO';

    const scopeParams = useCallback(() => {
        const data = {};
        if (isStandaloneScope && reviewScopeId) {
            data.review_id = reviewScopeId;
        } else if (templateId && templateId !== '0') {
            data.template_id = templateId;
        }
        return data;
    }, [isStandaloneScope, reviewScopeId, templateId]);

    const listBackUrl = isStandaloneScope && reviewScopeId
        ? `/performance/calibration/?review=${reviewScopeId}`
        : `/performance/calibration/?template=${templateId}`;

    const saveDraft = useCallback((objs, comps, reason, meeting, participants) => {
        if (readOnly) return;
        performanceService.saveCalibrationDraft({
            ...scopeParams(),
            employee_id: employeeId,
            assessment_id: assessmentId,
            objectives: objs,
            competencies: comps,
            general_reason: reason,
            committee_meeting_at: meeting || null,
            participant_ids: participants.map((p) => p.id),
        }).catch(() => {});
    }, [scopeParams, employeeId, assessmentId, readOnly]);

    const scheduleDraftSave = useCallback((objs, comps, reason, meeting, participants) => {
        if (draftTimer.current) clearTimeout(draftTimer.current);
        draftTimer.current = setTimeout(() => {
            saveDraft(objs, comps, reason, meeting, participants);
        }, 1500);
    }, [saveDraft]);

    const applyDetailData = useCallback((data) => {
        setDetail(data);
        setObjectives(data.objectives || []);
        setCompetencies(data.competencies || []);
        setGeneralReason(data.general_reason || '');
        setCommitteeMeetingAt(toDatetimeLocalValue(data.committee_meeting_at));
        setEligibleParticipants(data.eligible_participants || []);
        setSelectedParticipants(data.participants || []);
        setVersions(data.versions || []);
        setPreview({
            calibrated_scores: data.calibrated_scores,
            projected_matrix: data.projected_matrix,
        });
    }, []);

    const loadDetail = useCallback((recalibrate = false) => {
        performanceService.getCalibrationDetail({
            ...scopeParams(),
            employee_id: employeeId,
            assessment_id: assessmentId,
            recalibrate,
        }).then((data) => {
            applyDetailData(data);
        }).catch((error) => {
            toast.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: error.response?.data?.detail || 'Erro ao carregar dados',
                life: 5000,
            });
        }).finally(() => {
            setPageLoad(false);
        });
    }, [scopeParams, employeeId, assessmentId, applyDetailData]);

    const handleRecalibrate = () => {
        setPageLoad(true);
        loadDetail(true);
    };

    useEffect(() => {
        if (canAccess) loadDetail();
        else setPageLoad(false);
    }, [canAccess, loadDetail]);

    useEffect(() => () => {
        if (draftTimer.current) clearTimeout(draftTimer.current);
    }, []);

    const runPreview = useCallback((objs, comps) => {
        performanceService.previewCalibration({
            objectives: objs,
            competencies: comps,
            potential: detail?.potential,
        }).then((data) => {
            setPreview(data);
        }).catch(() => {});
    }, [detail?.potential]);

    function handleItemChange(idx, field, value, section) {
        const isObjective = section.includes('Objetivo');
        const current = isObjective ? objectives : competencies;
        const updated = current.map((item, i) => {
            if (i !== idx) return item;
            const next = { ...item, [field]: value };
            if (field === 'calibrated_score' && value === item.original_score) {
                next.justification = '';
            }
            return next;
        });

        const nextObjectives = isObjective ? updated : objectives;
        const nextCompetencies = isObjective ? competencies : updated;

        if (isObjective) setObjectives(updated);
        else setCompetencies(updated);

        runPreview(nextObjectives, nextCompetencies);
        scheduleDraftSave(nextObjectives, nextCompetencies, generalReason, committeeMeetingAt, selectedParticipants);
    }

    function handleParticipantsChange(value) {
        setSelectedParticipants(value);
        scheduleDraftSave(objectives, competencies, generalReason, committeeMeetingAt, value);
    }

    function handleMeetingChange(value) {
        setCommitteeMeetingAt(value);
        scheduleDraftSave(objectives, competencies, generalReason, value, selectedParticipants);
    }

    function handleReasonChange(value) {
        setGeneralReason(value);
        scheduleDraftSave(objectives, competencies, value, committeeMeetingAt, selectedParticipants);
    }

    function loadAllVersions() {
        performanceService.getCalibrationVersions({
            employee_id: employeeId,
            assessment_id: assessmentId,
        }).then((data) => {
            setVersions(data.versions || []);
            setShowVersions(true);
        });
    }

    function validate() {
        const allItems = [...objectives, ...competencies];
        for (const item of allItems) {
            if (
                detail?.requires_justification
                && item.original_score !== item.calibrated_score
                && !(item.justification || '').trim()
            ) {
                toast.current?.show({
                    severity: 'warn',
                    summary: 'Validação',
                    detail: `Justificativa obrigatória para: ${item.item_title}`,
                    life: 4000,
                });
                return false;
            }
        }
        if (!generalReason.trim()) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Validação',
                detail: 'Motivo geral da calibração é obrigatório',
                life: 4000,
            });
            return false;
        }
        return true;
    }

    function handleSubmit() {
        if (!validate()) return;
        setShowConfirm(true);
    }

    function confirmSubmit() {
        setSubmitting(true);
        performanceService.submitCalibration({
            ...scopeParams(),
            employee_id: employeeId,
            assessment_id: assessmentId,
            general_reason: generalReason,
            committee_meeting_at: committeeMeetingAt || null,
            participant_ids: selectedParticipants.map((p) => p.id),
            objectives,
            competencies,
        }).then((res) => {
            toast.current?.show({
                severity: 'success',
                summary: 'Sucesso',
                detail: `Calibração v${res.version || detail?.version || ''} registrada com sucesso`,
                life: 4000,
            });
            setShowConfirm(false);
            navigate(listBackUrl);
        }).catch((error) => {
            toast.current?.show({
                severity: 'error',
                summary: 'Erro',
                detail: error.response?.data?.detail || error.response?.data?.[0] || 'Erro ao salvar',
                life: 5000,
            });
        }).finally(() => {
            setSubmitting(false);
        });
    }

    function quadrantLabel(q) {
        if (!q) return '—';
        return `Q${q} - ${QUADRANT_LABELS[q] || q}`;
    }

    const participantOptions = eligibleParticipants.map((p) => ({
        id: p.id,
        name: p.name,
    }));

    if (!canAccess) {
        return (
            <ProtectedRoute>
                <div className="container-fluid p-5">
                    <p>Sem permissão para acessar calibração.</p>
                </div>
            </ProtectedRoute>
        );
    }

    return (
        <ProtectedRoute>
            <Toast ref={toast} position="top-right" />

            <div className="container-fluid paddingBottom200">
                <div className="row flex-nowrap">
                    <div className="col text-bg-green p-3">
                        <Link to={listBackUrl} className="fs-6 fw-bold ps-5 text-decoration-none text-white">
                            <FontAwesomeIcon icon={solid('arrow-left')} /> Calibração do Colaborador
                        </Link>
                    </div>
                </div>

                <div className="my-4 mx-5 py-3" style={{ minHeight: '500px' }}>
                    {pageLoad ? (
                        <SkeletonTeam />
                    ) : detail ? (
                        <>
                            <div className="d-flex justify-content-between align-items-start mb-4">
                                <div>
                                    <h4 className="text-green fw-bold mb-1">{detail.employee?.name}</h4>
                                    <p className="text-muted mb-0">
                                        {detail.employee?.jobposition?.title} — {detail.assessment_title}
                                        {detail.version && <span className="badge bg-secondary ms-2">v{detail.version}</span>}
                                    </p>
                                </div>
                                <div className="d-flex gap-2">
                                    {readOnly && detail?.can_recalibrate && (
                                        <Button
                                            label="Recalibrar"
                                            className="p-button-sm rounded-pill px-3"
                                            onClick={handleRecalibrate}
                                        />
                                    )}
                                    {versions.length > 0 && (
                                        <Button
                                            label={`Histórico (${versions.length})`}
                                            className="p-button-outlined p-button-sm rounded-pill px-3"
                                            onClick={loadAllVersions}
                                        />
                                    )}
                                </div>
                            </div>

                            {!readOnly && (
                                <div className="card p-4 mb-4">
                                    <h5 className="fw-bold text-green mb-3">Sessão do Comitê</h5>
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">Data/hora da reunião</label>
                                            <input
                                                type="datetime-local"
                                                className="form-control"
                                                value={committeeMeetingAt}
                                                onChange={(e) => handleMeetingChange(e.target.value)}
                                            />
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-bold">Participantes do comitê</label>
                                            <MultiSelect
                                                value={selectedParticipants}
                                                options={participantOptions}
                                                onChange={(e) => handleParticipantsChange(e.value)}
                                                optionLabel="name"
                                                dataKey="id"
                                                placeholder="Selecionar participantes"
                                                display="chip"
                                                className="w-100"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="row g-4">
                                <div className="col-lg-4">
                                    <div className="card p-4 h-100 border-secondary">
                                        <h5 className="fw-bold text-secondary mb-3">Notas Originais do Gestor</h5>
                                        <ItemTable title="Objetivos" items={objectives} readOnly company={company} showComments />
                                        <ItemTable title="Competências" items={competencies} readOnly company={company} showComments />
                                        <div className="border-top pt-3 mt-2">
                                            <p className="mb-2"><strong>Nota Final:</strong> <span className="bg-green-light-50 p-2 ms-2">{detail.original_scores?.final?.scorePercent?.toFixed(1)}</span></p>
                                            <p className="pt-2"><strong>Quadrante 9Box:</strong> {quadrantLabel(detail.original_matrix?.quadrant)}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-8">
                                    <div className="card p-4 h-100 border-success">
                                        <h5 className="fw-bold text-green mb-3">Notas Calibradas</h5>
                                        {!readOnly ? (
                                            <>
                                                <ItemTable
                                                    title="Objetivos"
                                                    items={objectives}
                                                    onItemChange={handleItemChange}
                                                    requiresJustification={detail.requires_justification}
                                                    company={company}
                                                />
                                                <ItemTable
                                                    title="Competências"
                                                    items={competencies}
                                                    onItemChange={handleItemChange}
                                                    requiresJustification={detail.requires_justification}
                                                    company={company}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <ItemTable title="Objetivos" items={objectives} readOnly company={company} />
                                                <ItemTable title="Competências" items={competencies} readOnly company={company} />
                                            </>
                                        )}
                                        <div className="border-top pt-3 mt-2 bg-light rounded p-3">
                                            <p className="mb-2"><strong>Nota Final Projetada:</strong> <span className="bg-green-light-50 p-2 ms-2">{preview?.calibrated_scores?.final?.scorePercent?.toFixed(1)}</span></p>
                                            <p className="pt-2"><strong>Quadrante Projetado:</strong> {quadrantLabel(preview?.projected_matrix?.quadrant)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {!readOnly && (
                                <div className="card p-4 mt-4">
                                    <label className="form-label fw-bold">
                                        Motivo geral da calibração <span className="text-danger">*</span>
                                    </label>
                                    <textarea
                                        className="form-control mb-3"
                                        rows={4}
                                        value={generalReason}
                                        onChange={(e) => handleReasonChange(e.target.value)}
                                        placeholder="Descreva o contexto e motivo geral desta calibração..."
                                    />
                                    <div className="d-flex gap-2">
                                        <Button
                                            label="Confirmar Calibração"
                                            className="btn btn-success btn-sm rounded-pill px-4 py-2"
                                            onClick={handleSubmit}
                                            loading={submitting}
                                        />
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="text-muted">Dados não encontrados.</p>
                    )}
                </div>
            </div>

            <Dialog
                header="Confirmar Calibração"
                visible={showConfirm}
                style={{ width: '450px' }}
                onHide={() => setShowConfirm(false)}
                footer={
                    <div>
                        <Button label="Confirmar e Aplicar" className="btn btn-success btn-sm rounded-pill px-4 py-2" onClick={confirmSubmit} loading={submitting} />
                    </div>
                }
            >
                <p>Deseja aplicar as notas calibradas e atualizar o quadrante 9Box deste colaborador?</p>
                <p className="mb-0 small text-muted">
                    Quadrante: {quadrantLabel(detail?.original_matrix?.quadrant)} → {quadrantLabel(preview?.projected_matrix?.quadrant)}
                </p>
            </Dialog>

            <Dialog
                header="Histórico de Versões"
                visible={showVersions}
                style={{ width: '700px' }}
                onHide={() => setShowVersions(false)}
            >
                {versions.length === 0 ? (
                    <p className="text-muted">Nenhuma versão anterior.</p>
                ) : (
                    versions.map((v) => (
                        <div key={v.session_id} className="border rounded p-3 mb-2">
                            <div className="d-flex justify-content-between">
                                <strong>Versão {v.version}</strong>
                                <small className="text-muted">
                                    {v.calibrated_by} — {v.calibrated_at ? new Date(v.calibrated_at).toLocaleString('pt-BR') : ''}
                                </small>
                            </div>
                            <p className="small mb-1">
                                {quadrantLabel(v.original_quadrant)} → {quadrantLabel(v.calibrated_quadrant)}
                                {' '}| {v.original_final_score?.toFixed(2)} → {v.calibrated_final_score?.toFixed(2)}
                            </p>
                            {v.committee_meeting_at && (
                                <p className="small text-muted mb-1">
                                    Reunião: {new Date(v.committee_meeting_at).toLocaleString('pt-BR')}
                                </p>
                            )}
                            <p className="small mb-0"><em>{v.general_reason}</em></p>
                        </div>
                    ))
                )}
            </Dialog>
        </ProtectedRoute>
    );
}
